import React, {
  useState,
  useEffect,
  useMemo,
  Fragment,
  useCallback,
  useRef,
  useContext,
} from "react";
import { NavLink } from "react-router-dom";
import {
  Autocomplete,
  Grid,
  TextField,
  CircularProgress,
  LinearProgress,
  Button,
  Collapse,
} from "@mui/material";
import ReactPaginate from "react-paginate";
import Table from "../../Components/Common/Table/Table";
import {
  getInfoMessageNoResultsSearch,
  CommonSubHeader,
} from "../../Components/helpers/Helpers";
import Swal from "sweetalert2";
import TableActions from "../../Components/Common/Table/TableActions";
import {
  Add,
  Delete,
  KeyboardArrowDown,
  KeyboardArrowUp,
  PostAdd,
} from "@mui/icons-material";
import api from "../../api/axios";
import { UserContext } from "../../Components/Context/Context";
import LoadingPage from "../../Components/Common/Loading/LoadingPage";
import { Box } from "@mui/system";
import DeleteCaseTypeModal from "./DeleteCaseTypeModal";
import { CustomButtonPlusIcon } from "../../Components/Common/Design/CustomButtonPlusIcon";
import { usePermissions } from "../../Components/Context/PermissionProvider";

function TiposDeCasos({ flagToShow = true }) {
  const { userData } = useContext(UserContext);
  const { user, userCompany } = userData;
  const { can } = usePermissions();

  const [casesTypes, setCasesTypes] = useState([]);
  const [objPerPage, setObjPerPage] = useState(10);
  const [pagePressed, setPagePressed] = useState(1);
  const [totalPagesNumber, setTotalPagesNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [flag, setFlag] = useState(false);
  const [changeDone, setChangeDone] = useState(false);

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  // Modal de eliminación con reasignación
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCaseTypeForDelete, setSelectedCaseTypeForDelete] =
    useState(null);

  const handleSort = (column, direction) => {
    console.log(column, direction);
    setSortColumn(column);
    setSortDirection(direction);
    setPagePressed(1); // reinicia paginación
  };

  const [filters, setFilters] = useState({
    company: 0,
    case_type: "",
  });

  const [options, setOptions] = useState({
    companies: [],
  });

  const [loadingOptions, setLoadingOptions] = useState({
    companies: false,
  });

  // Ref para debounce del input de nombre
  const nameTimeoutRef = useRef(null);

  const casesTypesData = useCallback(async () => {
    setFlag(true);
    try {
      const response = await api.get(
        process.env.REACT_APP_API_URL + "/casestypes",
        {
          params: {
            page: pagePressed,
            objPerPage,
            sortColumn,
            sortDirection,
            ...filters,
          },
        },
      );

      console.log("API response:", response.data);

      const { cases_types, companies } = response.data;

      companies.unshift({ company_name: "Seleccionar", id: 0 });

      setCasesTypes(cases_types.data);
      setPageCount(cases_types.last_page);
      setTotalPagesNumber(cases_types.total);
      setOptions((prev) => ({ ...prev, companies }));
    } catch (error) {
      console.error("ERROR AXIOS:", error);

      if (error.response) {
        console.error("STATUS:", error.response.status);
        console.error("DATA:", error.response.data);
        console.error("HEADERS:", error.response.headers);
      } else if (error.request) {
        console.error("NO RESPONSE:", error.request);
      } else {
        console.error("REQUEST ERROR:", error.message);
      }
      Swal.fire({
        icon: "error",
        title: "Error en el servidor",
        text: "Hubo un problema al cargar los datos.",
      });
    } finally {
      setFlag(false);
    }
  }, [pagePressed, objPerPage, filters, changeDone, sortColumn, sortDirection]);

  const handleDeleteClick = (caseTypeData) => {
    setSelectedCaseTypeForDelete(caseTypeData);
    setDeleteModalOpen(true);
  };

  useEffect(() => {
    casesTypesData();
  }, [casesTypesData]);

  const handlePageClick = (event) => setPagePressed(event.selected + 1);

  // Manejo de filtros
  const handleFilterChange = (name, value, isDebounced = false) => {
    if (name === "case_type") {
      // Si es el filtro de nombre, aplicamos debounce de 2 segundos
      if (nameTimeoutRef.current) clearTimeout(nameTimeoutRef.current);
      nameTimeoutRef.current = setTimeout(() => {
        setFilters((prev) => ({ ...prev, [name]: value }));
        setPagePressed(1);
        console.log("Filtro aplicado:", name, value);
      }, 2000);
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
      setPagePressed(1);
    }
  };

  const renderCellWithDefault = (value, defaultValue = "--") => (
    <div className="spanClassForTables" style={{ paddingLeft: "20px" }}>
      {value || defaultValue}
    </div>
  );

  const renderLogoCell = (value) => (
    <div className="spanClassForTables">
      {ImageCell(value) || "Sin asignar"}
    </div>
  );

  const ImageCell = (value) => {
    const [esImagenValida, setEsImagenValida] = useState(null);
    const url = `${process.env.REACT_APP_BACKEND_HOST}/storage/company/${value.id}/${value.company_logo}`;

    useEffect(() => {
      if (!url) return;
      const img = new Image();
      img.onload = () => setEsImagenValida(true);
      img.onerror = () => setEsImagenValida(false);
      img.src = url;
      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }, [url]);

    if (esImagenValida === null)
      return <div style={{ textAlign: "center" }}>Validando...</div>;

    return (
      <div
        style={{
          display: "inline-grid",
          // alignItems: "center",
          gap: "4px",
          // justifyContent: "start",
          // paddingLeft: "10px",
        }}>
        {esImagenValida === false ? (
          <div></div>
        ) : (
          <img src={url} alt="Company logo" style={{ height: "30px" }} />
        )}
        <b>{value.company_name}</b>
      </div>
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: "LISTA DE TIPOS DE CASOS",
        columns: [
          {
            Header: "Empresa",
            sortField: "company_name",
            accessor: "company_relation",
            width: 250,
            Cell: ({ value }) => renderLogoCell(value),
          },
          {
            Header: "Tipos de casos",
            sortField: "case_type",
            accessor: "case_type",
            width: 500,
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Acciones",
            accessor: "id",
            minWidth: 174,
            Cell: ({ value, row }) => (
              <div
                style={{
                  gap: 8,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                <TableActions
                  rute={"tiposdecasos"}
                  apiRute={"casestypes"}
                  module={"casestypes"}
                  completeValue={row.original}
                  value={value}
                  userLogged={user}
                  // showView
                  showEdit
                  setChangeDone={setChangeDone}
                  changeDone={changeDone}
                />

                {can("casestypes", "delete") && (
                  <CustomButtonPlusIcon
                    icon={Delete}
                    handleFunction={() => handleDeleteClick(row.original)}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                )}
              </div>
            ),
          },
        ],
      },
    ],
    [can, changeDone, user],
  );

  const fieldsConfig = [
    {
      type: "autocomplete",
      label: "Empresas",
      options: options.companies || [],
      loading: loadingOptions.companies,
      onChange: (_, value) =>
        handleFilterChange("company", value ? value.id : 0),
      getOptionLabel: (option) => option.company_name || "",
    },
    {
      type: "text",
      label: "Nombre",
      onChange: (e) => handleFilterChange("case_type", e.target.value || ""),
    },
  ];

  const renderField = (field, index) => (
    <Grid
      item
      xs={4}
      md={2}
      key={index}
      textAlign="left"
      sx={{ padding: "5px" }}>
      {field.type === "autocomplete" ? (
        <Autocomplete
          size="small"
          className={field.className}
          options={field.options || []}
          loading={field.loading}
          onChange={field.onChange}
          getOptionLabel={field.getOptionLabel}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.label}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {field.loading && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          )}
        />
      ) : (
        <TextField
          size="small"
          fullWidth
          label={field.label}
          onChange={field.onChange}
        />
      )}
    </Grid>
  );

  useEffect(() => {
    if (pagePressed > pageCount) setPagePressed(1);
  }, [pageCount]);

  const content = (
    <div className="fatherContainer">
      <div className="mainContainer">
        <div className="subMainContainer">
          <Box
            sx={{
              position: "relative",
              background: "white",
              marginBottom: "33px",
              mx: "10px",
              borderRadius: "10px",
            }}>
            <Collapse in={showFilters} timeout={400}>
              <Grid
                container
                className="filtersContainer"
                sx={{
                  boxShadow: `0 1px 2px rgba(0,0,0,0.05),0 -8px 38px rgba(0,0,0,0.08)`,
                  justifyContent: "center",
                }}>
                {fieldsConfig.map(renderField)}
              </Grid>
            </Collapse>
            {/* BOTÓN FLOTANTE */}
            <Box
              sx={{
                position: "absolute",
                bottom: showFilters ? "-26px" : "-16px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10,
              }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                endIcon={
                  showFilters ? <KeyboardArrowUp /> : <KeyboardArrowDown />
                }
                sx={{
                  minWidth: "220px",
                  height: "25px",
                  borderRadius: "0px 0px 10px 10px",
                  boxShadow: 3,
                  textTransform: "none",
                  fontWeight: 500,
                }}>
                {showFilters ? "Ocultar filtros" : "Mostrar filtros"}
              </Button>
            </Box>
          </Box>
          {(casesTypes === "" || flag) && (
            <LoadingPage isLoading heightProp={"60vh"} />
          )}

          {casesTypes && !flag && (
            <>
              <Table
                columns={columns}
                data={casesTypes}
                view={"tipo de caso"}
                viewTitle={"tiposdecasos"}
                totalPagesNumber={totalPagesNumber}
                icon={Add}
                apiRoute={"casestypes"}
                viewPermission={"casestypes"}
                // pagination
                onPageChange={handlePageClick}
                pageCount={pageCount}
                forcePage={pagePressed - 1} // Esto sincroniza el botón activo
                // SORT
                onSort={handleSort}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                tableWidth="unset"
                minTableWidth="80%"
                setChangeDone={setChangeDone}
                changeDone={changeDone}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {flagToShow ? (
        <div>
          <CommonSubHeader title="Configuración" subTitle="Tipos de casos" />
          {content}
        </div>
      ) : (
        <div className="fatherContainer">
          <div className="mainContainer">{content}</div>
        </div>
      )}

      {/* Modal de eliminación con reasignación */}
      {selectedCaseTypeForDelete && (
        <DeleteCaseTypeModal
          open={deleteModalOpen}
          caseTypeId={selectedCaseTypeForDelete.id}
          caseTypeName={selectedCaseTypeForDelete.case_type}
          companyId={selectedCaseTypeForDelete.company_id}
          onClose={() => {
            setDeleteModalOpen(false);
            setSelectedCaseTypeForDelete(null);
          }}
          onSuccess={() => {
            setChangeDone(!changeDone);
          }}
        />
      )}
    </>
  );
}

export default TiposDeCasos;
